import { FormEvent, useState } from 'react';
import { ApiError } from '../../services/api';
import { promotionsApi } from '../../services/promotionsApi';
import { PromoCodeDetail, PromoCustomer, ShareRecipient, ShareResult } from '../../types/promotions';
import CustomerPicker from './CustomerPicker';
import ShareResults from './ShareResults';
import { RadioGroup } from './PromoFormParts';
import { slotsLeft } from './promoFormat';
import {
  buildRecipients, ChannelChoice, INVALID_TARGET_MESSAGE, isValidEmail, isValidPhone, previewMessage,
  SHARE_BATCH_MAX, ShareTab, summarize,
} from './shareModel';

interface ShareModalProps {
  promo: PromoCodeDetail;
  token: string;
  onClose: () => void;
  /** Called once results are in (holders + slot counts changed); the string is the toast summary. */
  onShared: (summary: string) => void;
}

const TABS: { key: ShareTab; label: string }[] = [
  { key: 'CUSTOMERS', label: 'Customers' }, { key: 'EMAIL', label: 'Email' }, { key: 'PHONE', label: 'WhatsApp number' },
];

/** Spec 059 Flow C — share to existing customers, a typed email, or a typed WhatsApp number. */
export default function ShareModal({ promo, token, onClose, onShared }: ShareModalProps) {
  const [tab, setTab] = useState<ShareTab>('CUSTOMERS');
  const [customers, setCustomers] = useState<PromoCustomer[]>([]);
  const [channel, setChannel] = useState<ChannelChoice>('WHATSAPP');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState<ShareRecipient[]>([]);
  const [results, setResults] = useState<ShareResult[] | null>(null);

  const left = slotsLeft(promo);
  const max = Math.min(SHARE_BATCH_MAX, left);
  const pending = tab === 'CUSTOMERS' ? customers.length : 1;
  const withoutEmail = customers.filter((c) => !c.hasEmail).length;
  const noneHaveEmail = customers.length > 0 && withoutEmail === customers.length;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (sending) return;
    setError(null);
    setFieldError(null);
    if (tab === 'CUSTOMERS' && customers.length === 0) { setFieldError('Pick at least one customer.'); return; }
    if (tab === 'EMAIL' && !isValidEmail(email)) { setFieldError(INVALID_TARGET_MESSAGE); return; }
    if (tab === 'PHONE' && !isValidPhone(phone)) { setFieldError(INVALID_TARGET_MESSAGE); return; }
    const recipients = buildRecipients(tab, customers, noneHaveEmail && channel !== 'WHATSAPP' ? 'WHATSAPP' : channel, email, phone);
    setSending(true);
    try {
      const res = await promotionsApi.share(promo.id, recipients, token);
      setSent(recipients);
      setResults(res.results);
      onShared(summarize(res.results));
    } catch (err) {
      if (err instanceof ApiError && err.status === 422 && tab !== 'CUSTOMERS') setFieldError(INVALID_TARGET_MESSAGE);
      else setError(err instanceof Error ? err.message : 'Failed to share the code');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="admin-modal-overlay">
      <div className="admin-modal" role="dialog" aria-modal="true" aria-label={`Share ${promo.code}`} style={{ maxWidth: '620px', width: '100%' }}>
        <div className="admin-modal-header">
          <h3 style={{ margin: 0 }}>Share {promo.code}</h3>
          <button type="button" className="admin-modal-close" onClick={onClose} disabled={sending} aria-label="Close">&times;</button>
        </div>
        {results ? (
          <>
            <div className="admin-modal-body"><ShareResults results={results} sent={sent} customers={customers} /></div>
            <div className="admin-modal-footer"><button type="button" className="admin-btn admin-btn-primary" onClick={onClose}>Done</button></div>
          </>
        ) : (
          <form onSubmit={submit} noValidate>
            <div className="admin-modal-body">
              <div role="tablist" style={{ display: 'flex', gap: '0.25rem', borderBottom: '1px solid var(--admin-border)', marginBottom: '1rem' }}>
                {TABS.map((t) => (
                  <button key={t.key} type="button" role="tab" aria-selected={tab === t.key} className={`promo-tab${tab === t.key ? ' active' : ''}`}
                    disabled={sending} onClick={() => { setTab(t.key); setFieldError(null); }}>{t.label}</button>
                ))}
              </div>
              <div style={{ fontWeight: 600, marginBottom: '0.75rem' }} data-testid="slots-left">Slots left: {Math.max(0, left - pending)}</div>
              {error && <div className="admin-info-banner danger" role="alert" data-testid="share-error">{error}</div>}

              {tab === 'CUSTOMERS' && (
                <>
                  <CustomerPicker token={token} selected={customers} max={max} disabled={sending} onChange={setCustomers} />
                  <RadioGroup name="share-channel" label="Send by" value={noneHaveEmail && channel !== 'WHATSAPP' ? 'WHATSAPP' : channel} disabled={sending} onChange={setChannel}
                    options={[{ value: 'WHATSAPP', label: 'WhatsApp' }, ...(noneHaveEmail ? [] : [{ value: 'EMAIL' as const, label: 'Email' }, { value: 'BOTH' as const, label: 'Both' }])]} />
                  {withoutEmail > 0 && channel !== 'WHATSAPP' && (
                    <div className="admin-info-banner info" role="note" data-testid="no-email-note">
                      {withoutEmail} selected customer{withoutEmail === 1 ? ' has' : 's have'} no email — WhatsApp only for them.
                    </div>
                  )}
                </>
              )}
              {tab === 'EMAIL' && (
                <input type="email" className="admin-form-input" aria-label="Email address" placeholder="name@example.com" value={email}
                  disabled={sending} aria-invalid={!!fieldError} onChange={(e) => setEmail(e.target.value)} />
              )}
              {tab === 'PHONE' && (
                <input type="tel" className="admin-form-input" aria-label="WhatsApp number" placeholder="+971 5X XXX XXXX" value={phone}
                  disabled={sending} aria-invalid={!!fieldError} onChange={(e) => setPhone(e.target.value)} />
              )}
              {fieldError && <div role="alert" style={{ fontSize: '0.75rem', color: 'var(--admin-danger)', marginTop: '0.25rem' }}>{fieldError}</div>}

              <div style={{ marginTop: '1rem' }}>
                <div className="admin-form-label">Message preview</div>
                <div className="promo-preview" data-testid="share-preview">{previewMessage(promo)}</div>
              </div>
            </div>
            <div className="admin-modal-footer">
              <button type="button" className="admin-btn admin-btn-outline" onClick={onClose} disabled={sending}>Cancel</button>
              <button type="submit" className="admin-btn admin-btn-primary" disabled={sending || max <= 0}>{sending ? 'Sending…' : 'Send'}</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
